// src/components/auth/components/MiniOLSUpdates.tsx
import React, { useState } from "react";
import styled from "styled-components";
import { Typography, Button } from "antd";
import { Link } from "react-router-dom";
import { updates2900 } from "../../aols/aeo/OLSupdates/OLS2900/OLS2900";
import { upcomingUpdates } from "../../aols/aeo/OLSupdates/OLSupdatesUp";

const { Text } = Typography;

// Styled Components
const MiniContainer = styled.div`
  padding: 12px;
  background: #1a1a1a;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.7);
  }

  &:focus-within {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    padding: 10px;
    border-radius: 6px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
    gap: 6px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 6px;

  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const MiniTitle = styled(Text)`
  color: #ffa500;
  font-size: 16px;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  background-color: ${(props) => (props.$active ? "#ffaa00" : "#2a2a2a")};
  color: ${(props) => (props.$active ? "#000" : "#ffffff")};
  border: 1px solid #ffaa00;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  flex: 1;
  min-width: 80px; /* Ensure buttons don't shrink too much */

  &:hover {
    background-color: #ffc107;
    color: #000;
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 3px 6px;
    min-width: 70px;
  }
`;

const StyledButton = styled(Button)`
  background: #3552fc;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  transition: background 0.3s ease, transform 0.3s ease;
  flex: 1;
  min-width: 80px;

  &:hover {
    background: #2a43d6;
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    padding: 3px 6px;
    min-width: 70px;
  }
`;

const UpdateList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 8px;
  width: 100%;
  padding: 0;
  margin: 0;

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
    gap: 6px;
  }
`;

const UpdateItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const UpdateLink = styled.a`
  color: #ffaa00;
  font-size: 12px;
  text-decoration: none;
  text-align: center;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 0.3s ease, color 0.3s ease;

  &:hover {
    background: #ffaa00;
    color: #000;
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const InternalUpdateLink = styled(Link)`
  color: #ffaa00;
  font-size: 12px;
  text-decoration: none;
  text-align: center;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background 0.3s ease, color 0.3s ease;

  &:hover {
    background: #ffaa00;
    color: #000;
  }

  &:focus {
    outline: 2px solid #40c4ff;
    outline-offset: 2px;
  }

  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const Thumbnail = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ffaa00;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

const MiniOLSUpdates: React.FC = () => {
  const [viewMode, setViewMode] = useState<"latest" | "upcoming">("latest");

  // Get the last 12 updates for the grid, sorted by ID descending
  const latestUpdates = updates2900
    .slice()
    .sort((a, b) => b.id - a.id)
    .slice(0, 12);

  // Get the upcoming updates (sorted by date ascending)
  const upcoming = upcomingUpdates
    .slice()
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 12);

  const displayedUpdates = viewMode === "latest" ? latestUpdates : upcoming;

  return (
    <MiniContainer>
      <TitleWrapper>
        <MiniTitle>OLS Updates</MiniTitle>
        <ButtonWrapper>
          <ToggleButton
            $active={viewMode === "latest"}
            onClick={() => setViewMode("latest")}
            aria-label="View latest updates"
          >
            Latest
          </ToggleButton>
          <ToggleButton
            $active={viewMode === "upcoming"}
            onClick={() => setViewMode("upcoming")}
            aria-label="View upcoming updates"
          >
            Upcoming
          </ToggleButton>
          <StyledButton>
            <Link
              to="/olsupdates"
              style={{ color: "#ffffff", textDecoration: "none" }}
              aria-label="View all OLS updates"
            >
              All Updates
            </Link>
          </StyledButton>
        </ButtonWrapper>
      </TitleWrapper>
      <UpdateList>
        {displayedUpdates.map((update) => (
          <UpdateItem key={update.id}>
            {viewMode === "latest" ? (
              <InternalUpdateLink to={`/olsupdates/ols2900/${update.id}`}>
                <Thumbnail
                  src={update.thumbnail}
                  alt={`Thumbnail for OLS update ${update.text}`}
                />
                <span>{update.id}</span>
              </InternalUpdateLink>
            ) : (
              <UpdateLink href={update.url} target="_blank" rel="noopener noreferrer">
                <Thumbnail
                  src={update.thumbnail}
                  alt={`Thumbnail for OLS update ${update.text}`}
                />
                <span>{update.id}</span>
              </UpdateLink>
            )}
          </UpdateItem>
        ))}
      </UpdateList>
    </MiniContainer>
  );
};

export default React.memo(MiniOLSUpdates);